import { useRef, useState } from "react";

import { dayjs } from "neetocommons/utils";
import { Down, Left, Right } from "neetoicons";
import { Typography, Button, DatePicker } from "neetoui";
import { useTranslation } from "react-i18next";

import { getFormattedRangeStr } from "./utils";

const CustomDatePicker = ({
  selectedDate,
  setSelectedDate,
  picker,
  validDates,
  ...otherProps
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const datePickerRef = useRef(null);
  const buttonRef = useRef(null);

  const selectedRangeStr = getFormattedRangeStr(selectedDate, picker);

  const { start, end } = validDates;

  const isDateLessThanStart =
    start && selectedDate.isSameOrBefore(dayjs(start), picker);

  const isDateGreaterThanEnd =
    end && selectedDate.isSameOrAfter(dayjs(end), picker);

  const onPrevious = () =>
    setSelectedDate(selectedDate => selectedDate.subtract(1, picker));

  const onNext = () =>
    setSelectedDate(selectedDate => selectedDate.add(1, picker));

  const onToday = () => setSelectedDate(dayjs());

  const getDisabledDates = date => {
    if (!date) return false;

    return (start && date <= start) || (end && date >= end);
  };

  const handleButtonMouseDown = e => {
    if (!(buttonRef.current && buttonRef.current.contains(e.target))) return;
    e.stopPropagation();
    if (isOpen) datePickerRef.current.focus();
  };

  return (
    <div className="flex items-center justify-start gap-x-4">
      <Button
        aria-label={t("neetoMolecules.calendarView.aria.goToToday")}
        data-testid="neeto-molecules-calendar-view-today-button"
        label={t("neetoMolecules.calendarView.today")}
        style="secondary"
        onClick={onToday}
      />
      <div
        aria-label={t("neetoMolecules.calendarView.aria.dateNavigation")}
        className="flex"
        role="group"
      >
        <Button
          className="me-1"
          data-testid="neeto-molecules-calendar-view-previous-button"
          disabled={isDateLessThanStart}
          icon={() => <Left className="rtl:scale-x-[-1]" size={20} />}
          style="text"
          aria-label={t("neetoMolecules.calendarView.aria.previousPeriod", {
            period: picker,
          })}
          onClick={onPrevious}
        />
        <Button
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          className="min-w-52 justify-center"
          data-testid="neeto-molecules-calendar-view-date-picker-button"
          icon={Down}
          ref={buttonRef}
          style="text"
          aria-label={t("neetoMolecules.calendarView.aria.selectDate", {
            currentDate: selectedRangeStr,
          })}
          onClick={() => setIsOpen(isOpen => !isOpen)}
          onMouseDown={handleButtonMouseDown}
        >
          <Typography
            data-testid="neeto-molecules-calendar-view-date-picker-button-label"
            style="h5"
          >
            {selectedRangeStr}
          </Typography>
        </Button>
        <DatePicker
          className="invisible w-0 px-0"
          defaultValue={selectedDate}
          disabledDate={getDisabledDates}
          open={isOpen}
          placement="topRight"
          ref={datePickerRef}
          value={selectedDate}
          onChange={setSelectedDate}
          onOpenChange={setIsOpen}
          {...{ picker, ...otherProps }}
        />
        <Button
          className="ms-1"
          data-testid="neeto-molecules-calendar-view-next-button"
          disabled={isDateGreaterThanEnd}
          icon={() => <Right className="rtl:scale-x-[-1]" size={20} />}
          style="text"
          aria-label={t("neetoMolecules.calendarView.aria.nextPeriod", {
            period: picker,
          })}
          onClick={onNext}
        />
      </div>
    </div>
  );
};

export default CustomDatePicker;
